/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import PropTypes from 'prop-types';
// mui
import { Box, Typography, Tooltip } from '@mui/material';
// other libraries
import { isEmpty } from 'lodash';
// custom components
import { Iconify } from '../index';
// hooks
import useGetWidgetCounter, { useGetWidgetTeethCounter } from '../../hooks/useGetWidgetCounter';
// redux
import { useSelector } from '../../redux/store';
// utils
import { getTextColor } from '../../utils/getClassName';
import { getSelectedWidgetStyle } from '../../utils/reportWidgetUtils';
import {
    findWidgetTreatmentMatch,
    hasWidgetTreatmentMatch,
} from '../../utils/widgetTreatmentMatching';
// style
import { grey } from '../../theme/palette';
import {
    widgetCardWrapperStyle,
    widgetCounterTextStyle,
    widgetCounterWrapperStyle,
    widgetTextStyle,
    widgetCardStyle,
    widgetIconWrapper,
    getWidgetUrgencyBarColor,
    getWidgetBadgeStyle,
    widgetBadgeDotStyle,
    getUrgencyFromCount,
    getWidgetTextColor,
    getWidgetIconBgColor,
} from '../../styles/widgetCounterStyle';

const normaliseValues = (value) => {
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }
    if (value || value === 0) {
        return [value];
    }
    return [];
};

const getWidgetBadgeText = (count) => {
    if (count === 0) return 'All on track';
    if (count >= 10) return `${count} need action`;
    return `${count} to follow up`;
};

const valueIncludes = (value, target) => {
    const targetValue = target?.toString?.().toLowerCase?.() || '';
    return normaliseValues(value).some((val) => val?.toString?.().toLowerCase?.() === targetValue);
};

const CustomWidgetCard = ({ widgetArray, handleWidgetClick, currentWidget, counter, isTaskWidget, type }) => {
    const { tabCounter } = useGetWidgetCounter(counter);
    const { tabTeethCounter } = useGetWidgetTeethCounter(counter);
    const { treatmentsListForWidget: { results } } = useSelector((state) => state.practiceTreatment);

    const { widgetCounter } = useSelector((state) => state.widgetCounter);


    const getTreatmentIcon = (treatment) => {
        if (!isEmpty(results)) {
            return findWidgetTreatmentMatch(results, treatment)?.icon || null;
        }
        return null;
    };

    const getTreatmentAccentColor = (element) => {
        if (!isEmpty(results) && element.isTreatment) {
            return findWidgetTreatmentMatch(results, element.value)?.colour || null;
        }
        return null;
    };

    const getTreatmentColor = (element) => {
        if (!isEmpty(results) && element.isTreatment) {
            const color = findWidgetTreatmentMatch(results, element.value)?.colour;
            if (color) {
                return getTextColor(color);
            }
            return grey[800];
        }
        return grey[800];
    };

    const filteredWidgetCounter = widgetArray?.filter((ele) => (
        !ele.isTreatment || (ele.isTreatment && hasWidgetTreatmentMatch(results, ele?.value))
    ));

    const taskTabCounter = (key, dateFilter) => {
        const mapper = key + dateFilter;
        const count = widgetCounter.find(countArr => countArr.key === mapper);
        return count?.count || 0;
    };

    return <Box sx={widgetCardWrapperStyle}>
        {
            filteredWidgetCounter?.map((ele) => {
                const accentColor = getTreatmentAccentColor(ele);
                let counter = tabCounter(ele.key)?.[0]?.count || 0
                let teethCount = tabTeethCounter(ele.key)?.[0]?.teethCount || 0;
                let tooltipTitle = '';
                // Sub-treatment breakdown for treatment widgets
                let subChips = null;
                let showTeethBreakdown = false;
                if (type === "treatmentStarted" && valueIncludes(ele.value, 'invisalign')) {
                    const teethStraighteningCount = tabCounter("9")?.[0]?.count || 0;
                    const fixedBracesCount = tabCounter("10")?.[0]?.count || 0;
                    counter += teethStraighteningCount + fixedBracesCount;
                    teethCount = 0; // Aligners aren't per-tooth — hide teeth count
                    tooltipTitle = `Invisalign: ${tabCounter(ele.key)?.[0]?.count || 0} | Teeth Straightening: ${teethStraighteningCount} | Fixed Braces: ${fixedBracesCount}`;
                    subChips = [
                        { label: 'Invisalign', count: tabCounter(ele.key)?.[0]?.count || 0, color: findWidgetTreatmentMatch(results, 'invisalign')?.colour },
                        { label: 'Straightening', count: teethStraighteningCount, color: findWidgetTreatmentMatch(results, 'teeth straightening')?.colour },
                        { label: 'Fixed Braces', count: fixedBracesCount, color: findWidgetTreatmentMatch(results, 'fixed braces')?.colour },
                    ];
                }
                if (type === "treatmentStarted" && valueIncludes(ele.value, 'bonding')) {
                    const bondingCount = tabCounter(ele.key)?.[0]?.count || 0;
                    const bondingTeeth = tabTeethCounter(ele.key)?.[0]?.teethCount || 0;
                    const veneersCount = tabCounter("7")?.[0]?.count || 0;
                    const veneersTeeth = tabTeethCounter("7")?.[0]?.teethCount || 0;
                    counter += veneersCount;
                    teethCount += veneersTeeth;
                    showTeethBreakdown = true;
                    tooltipTitle = `Bonding: ${bondingCount} (${bondingTeeth} teeth) | Veneers: ${veneersCount} (${veneersTeeth} teeth)`;
                    subChips = [
                        { label: 'Bonding', count: bondingCount, teeth: bondingTeeth, color: findWidgetTreatmentMatch(results, 'bonding')?.colour },
                        { label: 'Veneers', count: veneersCount, teeth: veneersTeeth, color: findWidgetTreatmentMatch(results, 'veneers')?.colour },
                    ];
                }
                if (type === "treatmentStarted" && valueIncludes(ele.value, 'implants')) {
                    const implantsCount = tabCounter(ele.key)?.[0]?.count || 0;
                    const implantsTeeth = tabTeethCounter(ele.key)?.[0]?.teethCount || 0;
                    const allOn4Count = tabCounter("8")?.[0]?.count || 0;
                    const allOn4Teeth = tabTeethCounter("8")?.[0]?.teethCount || 0;
                    counter += allOn4Count;
                    teethCount += allOn4Teeth;
                    showTeethBreakdown = true;
                    tooltipTitle = `Implants: ${implantsCount} (${implantsTeeth} teeth) | All on 4: ${allOn4Count} (${allOn4Teeth} teeth)`;
                    subChips = [
                        { label: 'Implants', count: implantsCount, teeth: implantsTeeth, color: findWidgetTreatmentMatch(results, 'implants')?.colour },
                        { label: 'All on 4', count: allOn4Count, teeth: allOn4Teeth, color: findWidgetTreatmentMatch(results, 'all on 4')?.colour },
                    ];
                }
                const configAccent = ele.accentColor || null;
                const effectiveAccent = accentColor || configAccent;

                // Non-treatment cards: use urgency-based styling
                const displayCount = isTaskWidget ? taskTabCounter(ele.key, 'Today') : counter;
                const urgency = !ele.isTreatment && !ele.isPrice ? getUrgencyFromCount(displayCount) : null;
                const urgencyBarColor = urgency ? getWidgetUrgencyBarColor(urgency) : null;

                // Unified color system: icon, text, and number share one accent color
                const barColor = ele.isTreatment ? effectiveAccent : (urgencyBarColor || configAccent);
                const selectionAccent = ele.isTreatment ? (accentColor || getTreatmentColor(ele)) : (urgencyBarColor || configAccent);

                // Unified color system: icon, text, and number share one accent color
                let cardTextColor = '#475569';
                let cardIconBg = ele.bgColor || '#f1f5f9';
                if (ele.isTreatment) {
                    cardTextColor = getTreatmentColor(ele);
                    cardIconBg = accentColor ? `${accentColor}20` : '#f1f5f9';
                } else if (urgency) {
                    cardTextColor = getWidgetTextColor(urgency);
                    cardIconBg = getWidgetIconBgColor(urgency);
                }

                return <Box
                    key={ele.key}
                    sx={{
                        ...widgetCardStyle,
                        ...getSelectedWidgetStyle([ele.key, ...normaliseValues(ele.value)], currentWidget, selectionAccent),
                        ...(ele.noClick ? { cursor: 'default', '&:hover': { boxShadow: 'none' } } : {}),
                    }}
                    onClick={() => handleWidgetClick(ele.value, ele.key)}
                >
                    {/* Left accent bar */}
                    {barColor && barColor !== 'transparent' && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: '4px',
                            background: barColor,
                            borderRadius: '10px 0 0 10px',
                        }} />
                    )}
                    <Box sx={widgetCounterWrapperStyle}>
                        {isTaskWidget ? (
                            <Typography sx={{ ...widgetCounterTextStyle, color: cardTextColor }}>{taskTabCounter(ele.key, 'Today')}</Typography>
                        ) : (
                            <Tooltip title={tooltipTitle}>
                                <Typography sx={{ ...widgetCounterTextStyle, color: cardTextColor }}>{ele.isPrice ? `£${tabCounter(ele.key)?.[0]?.count || 0}` : counter}</Typography>
                            </Tooltip>
                        )}
                        {ele.isTreatment && getTreatmentIcon(ele.value) && (
                            <Box sx={{ ...widgetIconWrapper, bgcolor: cardIconBg }}>
                                <Iconify icon={getTreatmentIcon(ele.value)} width={16} height={16} sx={{ color: cardTextColor }} />
                            </Box>
                        )}
                        {!ele.isTreatment && ele.icon && (
                            <Box sx={{ ...widgetIconWrapper, bgcolor: cardIconBg }}>
                                <Iconify icon={ele.icon} width={16} height={16} sx={{ color: cardTextColor }} />
                            </Box>
                        )}
                    </Box>
                    <Typography sx={{ ...widgetTextStyle, color: cardTextColor }}>{ele.text}</Typography>
                    {/* Urgency badge for non-treatment cards */}
                    {!ele.isTreatment && urgency && !isTaskWidget && !ele.isPrice && (
                        <Box component="span" sx={getWidgetBadgeStyle(urgency)}>
                            <Box component="span" sx={widgetBadgeDotStyle(urgencyBarColor)} />
                            {getWidgetBadgeText(displayCount)}
                        </Box>
                    )}
                    {isTaskWidget && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', mt: '6px' }}>
                            <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '10px', background: '#f1f5f9', color: '#64748b', whiteSpace: 'nowrap' }}>
                                {`Upcoming: ${taskTabCounter(ele.key, 'Upcoming')}`}
                            </Box>
                        </Box>
                    )}
                    {/* Sub-treatment breakdown */}
                    {!isTaskWidget && subChips && subChips.length > 0 && (
                        <Box sx={{
                            mt: '8px', width: '100%',
                            background: '#f1f5f9',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            display: 'flex', flexDirection: 'column', gap: '3px',
                        }}>
                            {subChips.filter(s => s.count > 0).map((sub) => (
                                <Tooltip key={sub.label} title={showTeethBreakdown && sub.teeth != null ? `${sub.teeth} teeth` : ''}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                                            <Box sx={{
                                                width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
                                                bgcolor: sub.color || '#94a3b8',
                                            }} />
                                            <Box component="span" sx={{ fontSize: '10px', fontWeight: 500, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {sub.label}
                                            </Box>
                                        </Box>
                                        <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, color: '#1e293b', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                            {sub.count}
                                        </Box>
                                    </Box>
                                </Tooltip>
                            ))}
                            {/* Teeth Total divider row */}
                            {showTeethBreakdown && teethCount > 0 && (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    borderTop: '1px solid #e2e8f0', mt: '2px', pt: '4px',
                                }}>
                                    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#64748b', letterSpacing: '0.02em' }}>
                                        Teeth Total
                                    </Box>
                                    <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
                                        {teethCount}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* Simple teeth count for non-breakdown treatment cards (e.g., Whitening) */}
                    {!isTaskWidget && !ele.isPrice && !subChips && teethCount ? (
                        <Box sx={{
                            mt: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                            background: '#f1f5f9', borderRadius: '6px', padding: '5px 8px',
                        }}>
                            <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#64748b' }}>
                                Teeth Total
                            </Box>
                            <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
                                {teethCount}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            }
            )}
    </Box>;
};

export default CustomWidgetCard;

CustomWidgetCard.propTypes = {
    handleWidgetClick: PropTypes.func,
    widgetArray: PropTypes.any,
    currentWidget: PropTypes.string,
    counter: PropTypes.any,
    isTaskWidget: PropTypes.bool,
    type: PropTypes.string,
};
